/**
 * ATP/WTA 3-letter country code → emoji flag lookup.
 * Codes follow IOC/ITF standard used on ATP/WTA tour draws and rankings.
 */
export const COUNTRY_FLAG: Record<string, string> = {
  ALG: '🇩🇿', ARG: '🇦🇷', ARM: '🇦🇲', AUS: '🇦🇺', AUT: '🇦🇹',
  AZE: '🇦🇿', BAH: '🇧🇸', BEL: '🇧🇪', BIH: '🇧🇦', BLR: '🇧🇾',
  BRA: '🇧🇷', BUL: '🇧🇬', CAN: '🇨🇦', CHI: '🇨🇱', CHN: '🇨🇳',
  COL: '🇨🇴', CRO: '🇭🇷', CYP: '🇨🇾', CZE: '🇨🇿', DEN: '🇩🇰',
  ECU: '🇪🇨', EGY: '🇪🇬', ESP: '🇪🇸', EST: '🇪🇪', FIN: '🇫🇮',
  FRA: '🇫🇷', GBR: '🇬🇧', GEO: '🇬🇪', GER: '🇩🇪', GRE: '🇬🇷',
  HUN: '🇭🇺', IND: '🇮🇳', IRI: '🇮🇷', IRL: '🇮🇪', ISR: '🇮🇱',
  ITA: '🇮🇹', JPN: '🇯🇵', KAZ: '🇰🇿', KOR: '🇰🇷', LAT: '🇱🇻',
  LTU: '🇱🇹', LUX: '🇱🇺', MDA: '🇲🇩', MEX: '🇲🇽', MKD: '🇲🇰',
  MNE: '🇲🇪', MOR: '🇲🇦', NED: '🇳🇱', NOR: '🇳🇴', NZL: '🇳🇿',
  PAK: '🇵🇰', PER: '🇵🇪', POL: '🇵🇱', POR: '🇵🇹', PUR: '🇵🇷',
  ROU: '🇷🇴', RSA: '🇿🇦', RUS: '🇷🇺', SLO: '🇸🇮', SRB: '🇷🇸',
  SUI: '🇨🇭', SVK: '🇸🇰', SWE: '🇸🇪', THA: '🇹🇭', TPE: '🇹🇼',
  TUN: '🇹🇳', TUR: '🇹🇷', UKR: '🇺🇦', URU: '🇺🇾', USA: '🇺🇸',
  UZB: '🇺🇿', VEN: '🇻🇪',
};

/**
 * Returns the emoji flag for a 3-letter country code, or empty string if unknown.
 * Case-insensitive: 'ita' and 'ITA' both return '🇮🇹'.
 */
export function flagEmoji(code: string | undefined): string {
  if (!code) return '';
  return COUNTRY_FLAG[code.toUpperCase()] ?? '';
}

/**
 * Full cricket country/team name → emoji flag.
 * Franchise names (IPL, BBL, etc.) are not listed — cricketFlagEmoji returns '' for them.
 */
export const CRICKET_COUNTRY_FLAG: Record<string, string> = {
  'Afghanistan': '🇦🇫',
  'Australia': '🇦🇺',
  'Bangladesh': '🇧🇩',
  'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
  'India': '🇮🇳',
  'Ireland': '🇮🇪',
  'New Zealand': '🇳🇿',
  'Pakistan': '🇵🇰',
  'Scotland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  'South Africa': '🇿🇦',
  'Sri Lanka': '🇱🇰',
  'West Indies': '🏏',
  'Zimbabwe': '🇿🇼',
  'Netherlands': '🇳🇱',
  'Nepal': '🇳🇵',
  'Oman': '🇴🇲',
  'Papua New Guinea': '🇵🇬',
  'UAE': '🇦🇪',
  'United Arab Emirates': '🇦🇪',
  'USA': '🇺🇸',
  'United States': '🇺🇸',
  'Canada': '🇨🇦',
  'Kenya': '🇰🇪',
  'Namibia': '🇳🇦',
};

/** Returns flag emoji for a full cricket country name, or '' for franchise/unknown teams.
 *  Handles suffixes like "Women", "Men", "U19", "A" — e.g. "Australia Women" → 🇦🇺.
 */
export function cricketFlagEmoji(teamName: string | undefined): string {
  if (!teamName) return '';
  // Exact match first
  if (CRICKET_COUNTRY_FLAG[teamName]) return CRICKET_COUNTRY_FLAG[teamName];
  // Fuzzy: find the longest known country name contained within teamName
  const lower = teamName.toLowerCase();
  let best = '';
  let bestLen = 0;
  for (const country of Object.keys(CRICKET_COUNTRY_FLAG)) {
    if (lower.includes(country.toLowerCase()) && country.length > bestLen) {
      best = CRICKET_COUNTRY_FLAG[country];
      bestLen = country.length;
    }
  }
  return best;
}
